import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Dialog, DialogIberiaContent } from '../../../../../navigation/dialog/Dialog';
import styles from './PassengerCheckInUnavailableModal.module.css';
import { Link } from '../../../../../navigation/link/Link';
import { Text } from '../../../../../foundations/text/Text';

export type PassengerCheckInUnavailableModalProps = {
	open: boolean;
	onPrimaryAction: () => void;
	buttonText: ButtonText;
	fullName?: string;
	defaultOutsider?: boolean;
	isHidden?: boolean;
	showMessage?: boolean;
	message?: string;
};

type ButtonText = 'single' | 'multiple';

const handleUserInteraction = (onPrimaryAction: () => void) => (type: string) => {
	if (type === 'PRIMARY') {
		onPrimaryAction();
	}
};

const getButtonText = (buttonText: ButtonText): 'common:BTN_ACCEPT_CONTINUE' | 'common:BTN_ACCEPT_AND_EXIT' => {
	return buttonText === 'single' ? 'common:BTN_ACCEPT_AND_EXIT' : 'common:BTN_ACCEPT_CONTINUE';
};

export const PassengerCheckInUnavailableModal = ({
	onPrimaryAction,
	open,
	buttonText,
	fullName,
	defaultOutsider = false,
	isHidden = false,
	showMessage = false,
	message = ''
}: PassengerCheckInUnavailableModalProps) => {
	const { t } = useTranslation(['passenger-checkIn-unavailable-modal', 'common']);
	const [openDialog, setOpenDialog] = useState(open);
	const [messageError, setMessageError] = useState<string>('');
	const [linkInformation, setLinkInformation] = useState<{ href: string; text: string }>({ href: '', text: '' });
	useEffect(() => {
		setOpenDialog(open);
	}, [open]);

	useEffect(() => {
		if (showMessage && message) {
			const parser = new DOMParser();
			const doc = parser.parseFromString(message, 'text/html');

			const text = doc.body.textContent;
			const link = doc.body.querySelector('a');
			const linkContent = link?.textContent || '';

			setMessageError(text?.replace(linkContent, '') || '');
			setLinkInformation({ href: link?.href || '', text: link?.textContent || '' });
		} else {
			if (fullName) {
				setMessageError(t('BODY_CUSTOMIZED', { fullName }));
			} else {
				setMessageError(t('BODY_NOT_CUSTOMIZED'));
			}
		}
	}, [message, showMessage, fullName]);

	return (
		<Dialog open={openDialog} onOpenChange={setOpenDialog}>
			<DialogIberiaContent
				header={t('TITLE')}
				primaryButton={t(getButtonText(buttonText))}
				className={styles.modalContent}
				onUserInteraction={handleUserInteraction(onPrimaryAction)}
				defaultOutsider={defaultOutsider}
				isHidden={isHidden}
			>
				<p>
					{messageError}
					{showMessage && (
						<Link link={linkInformation.href} variant="tertiary" className={styles.modal_link} target="__blank">
							{linkInformation.text}
						</Link>
					)}
				</p>
				{!showMessage && (
					<Text font="verdana" size="lg-regular" as="p" className={styles.alert_description}>
						<Trans
							defaults="passenger-checkIn-unavailable-modal:TRAVEL_DOCUMENTATION_NOTICE"
							components={{
								1: (
									<Link
										data-testid="additional-info-link"
										link="https://www.iatatravelcentre.com/"
										target="__blank"
										variant="tertiary"
										className={styles.modal_link}
									/>
								)
							}}
						/>
					</Text>
				)}
			</DialogIberiaContent>
		</Dialog>
	);
};
